import { createReducer } from "typesafe-actions";

import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import Folder from "@mapmycustomers/shared/types/entity/Folder";
import Resource from "@mapmycustomers/shared/types/entity/Resource";

import resourceFieldModel, { ResourceFieldName } from "@app/util/fieldModel/ResourceFieldModel";

import {
  Actions,
  createFolder,
  deleteFolder,
  fetchPinnedResources,
  fetchResources,
  initialize,
  pinUnpinResource,
  updateFolder,
  uploadFiles,
} from "./actions";
import ResourcesViewState from "./ResourcesViewState";

const DEFAULT_PAGE_SIZE = 10;

interface ResourcesState {
  filesUploading: boolean;
  folders: Folder[];
  initializing: boolean;
  pinnedResources: Resource[];
  pinnedUpdating: boolean;
  resources: Resource[];
  resourcesLoading: boolean;
  storageUsed: number;
  total: number;
  viewState: ResourcesViewState;
}

const initialState: ResourcesState = {
  filesUploading: false,
  folders: [],
  initializing: false,
  pinnedResources: [],
  pinnedUpdating: false,
  resources: [],
  resourcesLoading: false,
  storageUsed: 0,
  total: 0,
  viewState: {
    columns: [],
    filter: {},
    range: { endRow: DEFAULT_PAGE_SIZE, startRow: 0 },
    sort: [
      { field: resourceFieldModel.getByName(ResourceFieldName.CREATED_AT)!, order: SortOrder.DESC },
    ],
    viewAs: undefined,
  },
};

const resources = createReducer<ResourcesState, Actions>(initialState)
  .handleAction(initialize.request, (state) => ({
    ...state,
    initializing: true,
  }))
  .handleAction(initialize.success, (state, { payload }) => ({
    ...state,
    folders: payload.folders,
    initializing: false,
    resources: payload.resources,
    storageUsed: payload.storageUsed,
  }))
  .handleAction(initialize.failure, (state) => ({
    ...state,
    initializing: false,
  }))
  .handleAction(fetchResources.request, (state, { payload }) => ({
    ...state,
    resourcesLoading: true,
    viewState: {
      ...state.viewState,
      ...payload,
      filter: payload.filter ? payload.filter : state.viewState.filter,
    },
  }))
  .handleAction(
    fetchResources.success,
    (state, { payload: { resources, storageUsed, totalResources } }) => ({
      ...state,
      resources,
      resourcesLoading: false,
      storageUsed,
      total: totalResources,
      viewState: { ...state.viewState },
    })
  )
  .handleAction(fetchResources.failure, (state) => ({
    ...state,
    resourcesLoading: false,
  }))
  .handleAction(uploadFiles.request, (state) => ({
    ...state,
    filesUploading: true,
  }))
  .handleAction(uploadFiles.success, (state) => ({
    ...state,
    filesUploading: false,
  }))
  .handleAction(uploadFiles.failure, (state) => ({
    ...state,
    filesUploading: false,
  }))
  .handleAction(createFolder.success, (state, { payload }) => ({
    ...state,
    folders: [...state.folders, payload],
  }))
  .handleAction(updateFolder.success, (state, { payload }) => ({
    ...state,
    folders: state.folders.map((folder) => (folder.id === payload.id ? payload : folder)),
  }))
  .handleAction(deleteFolder.success, (state, { payload }) => ({
    ...state,
    folders: state.folders.filter(({ id }) => id !== payload),
  }))
  .handleAction(fetchPinnedResources.success, (state, { payload }) => ({
    ...state,
    pinnedResources: payload,
  }))
  .handleAction(pinUnpinResource.request, (state) => ({
    ...state,
    pinnedUpdating: true,
  }))
  .handleAction(pinUnpinResource.success, (state, { payload }) => ({
    ...state,
    pinnedResources: payload,
    pinnedUpdating: false,
  }))
  .handleAction(pinUnpinResource.failure, (state) => ({
    ...state,
    pinnedUpdating: false,
  }));
export * from "./selectors";
export type ResourcesSceneActions = Actions;
export default resources;
